/**
 * Revenue Tab Component for Admin Panel
 */
import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  DollarSign, Calendar, Users, Loader2, Receipt, FileText,
  FileCheck, Mail, Printer, Coins
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from './adminHelpers';

export const RevenueTab = ({
  revenueSummary,
  revenueCustomers,
  revenueLoading,
  selectedRevenueCustomer,
  customerPayments,
  fetchCustomerPayments,
  generateInvoice
}) => {
  return (
    <>
      {/* Revenue Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Total Revenue', value: `₹${(revenueSummary.total_revenue_inr || 0).toLocaleString()}`, color: 'border-emerald-500', textColor: 'text-emerald-700', icon: DollarSign },
          { label: 'This Month', value: `₹${(revenueSummary.current_month_inr || 0).toLocaleString()}`, color: 'border-blue-500', textColor: 'text-blue-700', icon: Calendar },
          { label: 'Credit Purchases', value: `₹${(revenueSummary.credit_purchases_total || 0).toLocaleString()}`, color: 'border-purple-500', textColor: 'text-purple-700', icon: Coins },
          { label: 'Design Services', value: `₹${(revenueSummary.design_services_total || 0).toLocaleString()}`, color: 'border-amber-500', textColor: 'text-amber-700', icon: FileCheck }
        ].map((stat, i) => (
          <Card key={i} className={`border-l-4 ${stat.color} bg-white`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-zinc-500 text-sm">{stat.label}</p>
                  <p className={`text-2xl font-bold ${stat.textColor}`}>{stat.value}</p>
                </div>
                <stat.icon className={`w-8 h-8 ${stat.textColor} opacity-40`} />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Customer List */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="w-4 h-4" /> Customers
            </CardTitle>
          </CardHeader>
          <CardContent>
            {revenueLoading ? (
              <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div>
            ) : (
              <div className="space-y-2 max-h-[500px] overflow-y-auto">
                {revenueCustomers.map(customer => (
                  <div 
                    key={customer.user_id}
                    className={`p-3 rounded-lg border cursor-pointer transition ${selectedRevenueCustomer?.user_id === customer.user_id ? 'border-emerald-300 bg-emerald-50' : 'border-zinc-200 hover:bg-zinc-50'}`}
                    onClick={() => fetchCustomerPayments(customer.user_id)}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-sm">{customer.name || customer.email}</p>
                        <p className="text-xs text-zinc-500">{customer.email}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-emerald-600">₹{(customer.total_inr || 0).toLocaleString()}</p>
                        <p className="text-xs text-zinc-400">{customer.transaction_count} payments</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Payment Timeline */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Receipt className="w-4 h-4" /> 
              {selectedRevenueCustomer ? `Payments - ${selectedRevenueCustomer.name || selectedRevenueCustomer.email}` : 'Select a customer'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {selectedRevenueCustomer ? (
              <div className="space-y-3 max-h-[500px] overflow-y-auto">
                {customerPayments.length === 0 ? (
                  <div className="text-center py-12 text-zinc-500">
                    <Receipt className="w-10 h-10 mx-auto mb-2 text-zinc-300" />
                    <p>No payments found</p>
                  </div>
                ) : customerPayments.map(payment => (
                  <PaymentCard 
                    key={payment.payment_id}
                    payment={payment}
                    generateInvoice={generateInvoice}
                  />
                ))}
                
                {/* Action Buttons for selected customer */}
                <CustomerActions 
                  customer={selectedRevenueCustomer}
                  fetchCustomerPayments={fetchCustomerPayments}
                />
              </div>
            ) : (
              <div className="text-center py-12 text-zinc-500">
                <Users className="w-10 h-10 mx-auto mb-2 text-zinc-300" />
                <p className="text-sm">Select a customer to view their payment history</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
};

// Payment Card Sub-component
const PaymentCard = ({ payment, generateInvoice }) => (
  <div className="p-4 border rounded-lg">
    <div className="flex items-start justify-between">
      <div>
        <div className="flex items-center gap-2">
          <span className={`px-2 py-0.5 rounded text-xs ${
            payment.type === 'credits' || payment.payment_type === 'credits' ? 'bg-purple-100 text-purple-700' : 'bg-amber-100 text-amber-700'
          }`}>
            {payment.type || payment.payment_type || 'Payment'}
          </span>
          <span className="text-xs text-zinc-400">
            {payment.paid_at ? new Date(payment.paid_at).toLocaleDateString() : '-'}
          </span>
        </div>
        <p className="font-bold text-lg mt-1">₹{(payment.amount_inr || payment.amount || 0).toLocaleString()}</p>
        <p className="text-xs text-zinc-500 mt-1">
          {payment.description || (payment.credits_added ? `${payment.credits_added} credits` : 'Payment')}
        </p>
        <p className="text-xs text-zinc-400 mt-2">
          Razorpay: {payment.razorpay_payment_id || 'N/A'}
        </p>
      </div>
      <div className="flex flex-col gap-2">
        <Button 
          size="sm" 
          variant="outline"
          onClick={() => generateInvoice(payment.payment_id)}
        >
          <FileCheck className="w-4 h-4 mr-1" /> Invoice
        </Button>
        <Button 
          size="sm" 
          variant="ghost"
          onClick={() => window.open(`${API}/revenue/invoices/${payment.invoice_id || payment.payment_id}/pdf`, '_blank')}
        >
          <Printer className="w-4 h-4 mr-1" /> PDF
        </Button>
        <Button 
          size="sm" 
          variant="ghost"
          className="text-blue-600"
          onClick={async () => {
            try {
              await axios.post(`${API}/revenue/invoices/${payment.invoice_id || payment.payment_id}/email`, {}, { withCredentials: true });
              toast.success('Invoice email queued');
            } catch (e) { toast.error('Failed to queue email'); }
          }}
        >
          <Mail className="w-4 h-4 mr-1" /> Email
        </Button>
      </div>
    </div>
  </div>
);

// Customer Actions Sub-component
const CustomerActions = ({ customer, fetchCustomerPayments }) => {
  const handleCreditNote = async () => {
    const reason = prompt('Enter reason for credit note/refund:');
    if (!reason) return;
    const amount = prompt('Enter amount (₹):');
    if (!amount || isNaN(parseFloat(amount))) return;
    const processRefund = window.confirm('Process Razorpay refund? Click OK to refund via Razorpay, Cancel for credit note only.');
    
    try {
      const cnResponse = await axios.post(`${API}/revenue/credit-notes`, {
        customer_user_id: customer.user_id,
        amount: parseFloat(amount),
        reason: reason,
        description: `Credit Note: ${reason}`,
        process_refund: processRefund
      }, { withCredentials: true });
      
      toast.success('Credit note created');
      
      if (processRefund) {
        try {
          const refundResponse = await axios.post(`${API}/revenue/credit-notes/${cnResponse.data.credit_note_id}/process-refund`, {}, { withCredentials: true });
          if (refundResponse.data.success) {
            toast.success(`Refund processed: ₹${refundResponse.data.amount}`);
          } else {
            toast.info(refundResponse.data.message);
          }
        } catch (refundErr) {
          toast.error('Refund failed - credit note created but manual refund needed');
        }
      }
      
      fetchCustomerPayments(customer.user_id);
    } catch (e) {
      toast.error('Failed to create credit note');
    }
  };

  return (
    <div className="pt-4 border-t mt-4 flex flex-wrap gap-2">
      <Button 
        variant="outline" 
        size="sm"
        onClick={() => window.open(`${API}/revenue/customer/${customer.user_id}/statement/pdf`, '_blank')}
      >
        <FileText className="w-4 h-4 mr-1" /> Statement PDF
      </Button>
      <Button 
        variant="outline" 
        size="sm"
        onClick={() => window.open(`${API}/revenue/customer/${customer.user_id}/statement/excel`, '_blank')}
      >
        <FileText className="w-4 h-4 mr-1" /> Statement Excel
      </Button>
      <Button 
        variant="outline" 
        size="sm"
        className="text-blue-600"
        onClick={async () => {
          try {
            await axios.post(`${API}/revenue/customer/${customer.user_id}/statement/email`, { formats: ['pdf', 'excel'] }, { withCredentials: true });
            toast.success('Statement email queued');
          } catch (e) { toast.error('Failed to queue email'); }
        }}
      >
        <Mail className="w-4 h-4 mr-1" /> Email Statement
      </Button>
      <Button 
        variant="outline" 
        size="sm"
        className="text-red-600 border-red-200"
        onClick={handleCreditNote}
      >
        <Receipt className="w-4 h-4 mr-1" /> Issue Credit Note / Refund
      </Button>
    </div>
  );
};

export default RevenueTab;
